import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded"
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded"
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { subYears } from "date-fns"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import Column from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import { LoadingTextSkeleton } from "../../../../../common/components/TextSkeleton"
import { formatCurrency } from "../../../../../common/utils"
import BorderedGrayChip from "../../../components/BorderedGrayChip"
import ProductionOverTimeChart from "./ProductionOverTimeChart"
import SalesVolumeChart from "./SalesVolumeChart"

export default function SalesPerformanceTab({
  candidate,
  selectedTimePeriod,
  setSelectedTimePeriod,
  salesVolume,
  isLoadingSalesVolume,
  averageSales,
  isLoadingAverageSales,
  closedToListPriceRatio,
  isLoadingClosedToListPriceRatio,
  averageDaysOnMarket,
  isLoadingAverageDaysOnMarket,
  unitsClosed,
  isLoadingUnitsClosed,
  rentals,
  isLoadingRentals,
  currentActivity,
  isLoadingCurrentActivity,
  salesVolumeChartData,
  isLoadingSalesVolumeChart,
  salesVolumeSidesChartData,
  isLoadingSalesVolumeSidesChartData,
}) {
  const gciMultiplier = 0.0275

  return (
    <Box
      sx={{
        position: "relative",
        overflowY: "auto",
        overflowX: "hidden",
        padding: 1,
        paddingTop: 2,
      }}
    >
      <Box>
        {isLoadingSalesVolumeChart ? (
          <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Card>
        ) : (
          <SalesVolumeChart
            candidate={candidate}
            salesVolumeChartData={salesVolumeChartData}
            isLoadingSalesVolumeChart={isLoadingSalesVolumeChart}
          />
        )}
      </Box>
      <Box sx={{ mt: 3, mb: 4 }}>
        {isLoadingSalesVolumeSidesChartData ? (
          <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={220} />
          </Card>
        ) : (
          <ProductionOverTimeChart
            salesVolumeSidesChartData={salesVolumeSidesChartData}
            isLoadingSalesVolumeSidesChartData={
              isLoadingSalesVolumeSidesChartData
            }
          />
        )}
      </Box>

      {/* Time Period Dropdown */}
      <Row
        sx={{ justifyContent: "flex-end", alignItems: "center", mt: 3, mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            label="Time Period"
            value={selectedTimePeriod}
            onChange={(e) => setSelectedTimePeriod(e.target.value)}
          >
            {TIME_PERIOD_OPTIONS.map((option, i) => (
              <MenuItem key={i} value={option}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Row>

      <Grid container spacing={2}>
        <MetricCard
          title="Sales Volume"
          currentPeriodValue={salesVolume.current_period}
          prevPeriodValue={salesVolume.prev_period}
          isLoading={isLoadingSalesVolume}
        />
        <MetricCard
          title="Average Sales Price"
          currentPeriodValue={averageSales.current_period}
          prevPeriodValue={averageSales.prev_period}
          isLoading={isLoadingAverageSales}
        />
        <MetricCard
          title="Approx. GCI"
          currentPeriodValue={salesVolume.current_period * gciMultiplier}
          prevPeriodValue={salesVolume.prev_period * gciMultiplier}
          isLoading={isLoadingSalesVolume}
        />
      </Grid>

      <Box sx={{ mt: 1 }}>
        <Grid container spacing={2}>
          <Grid xs={12} sm={6}>
            <TitleValueCard
              title="Closed/List Price"
              value={closedToListPriceRatio.value.toLocaleString(undefined, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
              isLoading={isLoadingClosedToListPriceRatio}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <TitleValueCard
              title="Average Days on Market"
              value={Math.round(averageDaysOnMarket.value)}
              isLoading={isLoadingAverageDaysOnMarket}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <DividedContentCard
              title="Units Closed"
              mainValue={(
                unitsClosed.listing + unitsClosed.selling
              ).toLocaleString()}
              mainRatio={
                (unitsClosed.listing + unitsClosed.selling) /
                unitsClosed.total_sold_or_not
              }
              BottomLeftContent={
                <DividedContentCardSide
                  label="Buy-side"
                  value={unitsClosed.selling.toLocaleString()}
                />
              }
              BottomRightContent={
                <DividedContentCardSide
                  label="List-side"
                  value={unitsClosed.listing.toLocaleString()}
                />
              }
              isLoading={isLoadingUnitsClosed}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DividedContentCard
              title="Rental Transactions"
              mainValue={rentals.rentals.count}
              mainRatio={rentals.rentals.count / rentals.total}
              BottomLeftContent={
                <DividedContentCardSide
                  label="Average price"
                  value={`$${Math.round(
                    rentals.rentals.avg_rent
                  ).toLocaleString()}`}
                />
              }
              isLoading={isLoadingRentals}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={500}
            sx={{ mb: 2 }}
          >
            Current Activity
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <ActivitySection
              title="Active Listings"
              activity={currentActivity.active}
              isLoading={isLoadingCurrentActivity}
            />
            <ActivitySection
              title="Pending Listings"
              activity={currentActivity.pending}
              isLoading={isLoadingCurrentActivity}
            />
            <ActivitySection
              title="Under Contract"
              activity={currentActivity.under_contract}
              isLoading={isLoadingCurrentActivity}
              addBorder={false}
            />
          </Grid>
        </Card>
      </Box>
    </Box>
  )
}

function MetricCard({ title, currentPeriodValue, prevPeriodValue, isLoading }) {
  const isNegative = currentPeriodValue < prevPeriodValue
  const icon = isNegative ? (
    <ArrowDownwardRoundedIcon fontSize="small" color="error" />
  ) : (
    <ArrowUpwardRoundedIcon fontSize="small" color="success" />
  )

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          height: "100%",
        }}
      >
        <CardContent>
          <Column sx={{ gap: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {title}
            </Typography>
            <LoadingTextSkeleton isLoading={isLoading} width={8} height="1em">
              <Typography variant="h4" fontWeight={700}>
                ${Math.round(currentPeriodValue).toLocaleString()}
              </Typography>
            </LoadingTextSkeleton>
            <LoadingTextSkeleton isLoading={isLoading} width="3" height="1em">
              <Row sx={{ alignItems: "center", gap: 0.5 }}>
                {icon}
                <Typography
                  variant="caption"
                  color={isNegative ? "error.main" : "success.main"}
                  fontWeight={600}
                >
                  {Math.min(
                    Math.round(
                      (100 * (currentPeriodValue - prevPeriodValue)) /
                        prevPeriodValue
                    ),
                    100
                  )}
                  %
                </Typography>
              </Row>
            </LoadingTextSkeleton>
          </Column>
        </CardContent>
      </Card>
    </Grid>
  )
}

function TitleValueCard({ title, value, isLoading }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          fontWeight={500}
        >
          {title}
        </Typography>
        <LoadingTextSkeleton width={4} height="2em" isLoading={isLoading}>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
        </LoadingTextSkeleton>
      </CardContent>
    </Card>
  )
}

function DividedContentCard({
  title,
  mainValue,
  mainRatio,
  BottomLeftContent,
  BottomRightContent,
  isLoading,
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Row sx={{ justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <LoadingTextSkeleton width={3} height="1em" isLoading={isLoading}>
            <StyledBorderedGrayChip label={`${Math.round(mainRatio * 100)}%`} />
          </LoadingTextSkeleton>
        </Row>
        <LoadingTextSkeleton width={2} height="2em" isLoading={isLoading}>
          <Typography variant="h5" fontWeight={700}>
            {mainValue}
          </Typography>
        </LoadingTextSkeleton>
        <Divider sx={{ my: 1 }} />
        <Row sx={{ justifyContent: "space-between" }} mt={1}>
          <LoadingTextSkeleton width={8} height="1em" isLoading={isLoading}>
            {BottomLeftContent}
          </LoadingTextSkeleton>
          {BottomRightContent}
        </Row>
      </CardContent>
    </Card>
  )
}

function DividedContentCardSide({ label, value }) {
  return (
    <Typography variant="caption" color="text.secondary">
      <b>{value}</b> {label}
    </Typography>
  )
}

function ActivitySection({ title, activity, isLoading, addBorder = true }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Column
        sx={{
          gap: 0.5,
          alignItems: "flex-start",
          borderRight: (theme) =>
            addBorder ? `1px solid ${theme.palette.divider}` : "none",
        }}
      >
        <LoadingTextSkeleton width={10} height="1em" isLoading={isLoading}>
          <Typography variant="body1" fontWeight={700}>
            {activity.count.toLocaleString()} {title}
          </Typography>
        </LoadingTextSkeleton>
        <LoadingTextSkeleton width={5} height="1em" isLoading={isLoading}>
          <StyledBorderedGrayChip label={formatCurrency(activity.production)} />
        </LoadingTextSkeleton>
      </Column>
    </Grid>
  )
}

const StyledBorderedGrayChip = styled(BorderedGrayChip)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
}))

class TimePeriodOption {
  constructor(label, queryName, queryValue) {
    this.label = label
    this.queryName = queryName
    this.queryValue = queryValue
  }

  static getYearsBackOption(yearsBack) {
    const year = new Date().getFullYear() - yearsBack
    return new TimePeriodOption(`${year}`, "year", year)
  }
}

const TIME_PERIOD_OPTIONS = [
  new TimePeriodOption("LTM (Last Twelve Months)", "ltm"),
  new TimePeriodOption("Current year", "ytd"),
  TimePeriodOption.getYearsBackOption(1),
  TimePeriodOption.getYearsBackOption(2),
  new TimePeriodOption("5 Years", "year_count", 5),
  new TimePeriodOption("All Time", "all_t"),
]

export function useSalesPerformanceTabState(ssotAgentID) {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    TIME_PERIOD_OPTIONS[0]
  )
  const METRIC_INITIAL_VALUE = {
    current_period: 0,
    prev_period: 0,
  }
  const [salesVolume, setSalesVolume] = useState(METRIC_INITIAL_VALUE)
  const [isLoadingSalesVolume, setIsLoadingSalesVolume] = useState(false)
  const [averageSales, setAverageSales] = useState(METRIC_INITIAL_VALUE)
  const [isLoadingAverageSales, setIsLoadingAverageSales] = useState(false)
  const [closedToListPriceRatio, setClosedToListPriceRatio] = useState({
    value: 0,
  })
  const [isLoadingClosedToListPriceRatio, setIsLoadingClosedToListPriceRatio] =
    useState(false)
  const [averageDaysOnMarket, setAverageDaysOnMarket] = useState({ value: 0 })
  const [isLoadingAverageDaysOnMarket, setIsLoadingAverageDaysOnMarket] =
    useState(false)
  const [unitsClosed, setUnitsClosed] = useState({
    listing: 0,
    selling: 0,
    total_sold_or_not: 0,
  })
  const [isLoadingUnitsClosed, setIsLoadingUnitsClosed] = useState(false)
  const [rentals, setRentals] = useState({
    rentals: {
      count: 0,
      avg_rent: 0,
    },
    total: 0,
  })
  const [isLoadingRentals, setIsLoadingRentals] = useState(false)
  const [currentActivity, setCurrentActivity] = useState({
    active: new ActivityPeriod(),
    pending: new ActivityPeriod(),
    under_contract: new ActivityPeriod(),
  })
  const [isLoadingCurrentActivity, setIsLoadingCurrentActivity] =
    useState(false)

  const [salesVolumeChartData, setSalesVolumeChartData] = useState({
    history_start_date: new Date(),
    history_end_date: subYears(new Date(), 10),
    annual_productions: [],
    annual_productions_similar_agents: [],
    office_movements: [],
  })
  const [isLoadingSalesVolumeChart, setIsLoadingSalesVolumeChart] =
    useState(false)
  const [salesVolumeSidesChartData, setSalesVolumeSidesChartData] = useState({
    listing: [],
    selling: [],
  })
  const [
    isLoadingSalesVolumeSidesChartData,
    setIsLoadingSalesVolumeSidesChartData,
  ] = useState(false)

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/sales-volume/${ssotAgentID}/`,
    setIsLoadingSalesVolume,
    setSalesVolume
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/avg-sale-price/${ssotAgentID}/`,
    setIsLoadingAverageSales,
    setAverageSales
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/closed-to-list-price/${ssotAgentID}/`,
    setIsLoadingClosedToListPriceRatio,
    setClosedToListPriceRatio
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/avg-days-on-market/${ssotAgentID}/`,
    setIsLoadingAverageDaysOnMarket,
    setAverageDaysOnMarket
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/units-closed/${ssotAgentID}/`,
    setIsLoadingUnitsClosed,
    setUnitsClosed
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/tx/rental/${ssotAgentID}/`,
    setIsLoadingRentals,
    setRentals
  )

  useQueryMetric(
    ssotAgentID,
    selectedTimePeriod,
    `/realitydb/api/candidate-profile/current-activity/${ssotAgentID}/`,
    setIsLoadingCurrentActivity,
    setCurrentActivity
  )

  useLoadChartData(
    ssotAgentID,
    `/realitydb/api/candidate-profile/sales-volume/chart/${ssotAgentID}/`,
    setIsLoadingSalesVolumeChart,
    setSalesVolumeChartData
  )

  useLoadChartData(
    ssotAgentID,
    `/realitydb/api/candidate-profile/sales-volume/sides/${ssotAgentID}/`,
    setIsLoadingSalesVolumeSidesChartData,
    setSalesVolumeSidesChartData
  )

  return {
    selectedTimePeriod,
    setSelectedTimePeriod,
    salesVolume,
    isLoadingSalesVolume,
    averageSales,
    isLoadingAverageSales,
    closedToListPriceRatio,
    isLoadingClosedToListPriceRatio,
    averageDaysOnMarket,
    isLoadingAverageDaysOnMarket,
    unitsClosed,
    isLoadingUnitsClosed,
    rentals,
    isLoadingRentals,
    currentActivity,
    isLoadingCurrentActivity,
    salesVolumeChartData,
    isLoadingSalesVolumeChart,
    salesVolumeSidesChartData,
    isLoadingSalesVolumeSidesChartData,
  }
}

function useQueryMetric(
  ssotAgentID,
  selectedTimePeriod,
  apiURL,
  setIsLoading,
  setMetricValue
) {
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!ssotAgentID) {
        return
      }
      setIsLoading(true)
      axios
        .get(apiURL, {
          params: {
            tqt: selectedTimePeriod.queryName,
            tqv: selectedTimePeriod.queryValue,
          },
        })
        .then(({ data }) => setMetricValue(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ssotAgentID, selectedTimePeriod]
  )
}

function useLoadChartData(ssotAgentID, apiURL, setIsLoading, setChartData) {
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!ssotAgentID) {
        return
      }
      setIsLoading(true)
      axios
        .get(apiURL)
        .then(({ data }) => setChartData(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ssotAgentID]
  )
}

class ActivityPeriod {
  constructor(count = 0, production = 0) {
    this.count = count
    this.production = production
  }
}
